import StringUtils from "@/utils/StringUtils";
import _ from "lodash";

const REGEX_USER_MENTION =
  /<span class="mention".*?data-object-id="([0-9a-f]+)".*?data-mention-type="user".*?<\/span>/g;
const REGEX_TEAM_MENTION =
  /<span class="mention".*?data-object-id="([0-9a-f]+)".*?data-mention-type="team".*?<\/span>/g;
const REGEX_BLOCK_QUOTE = /<blockquote>.*?<\/blockquote>/g;

const REGEX_VARIABLE =
  /<span class="mention-variable".*?data-variable-id="([\w]+)".*?<\/span>/g;

class TextEditorUtilsClass {
  replacePlaceholdersInHtml(
    text: string,
    variableId: string,
    replaceWith: string
  ) {
    const regex = new RegExp(
      `<span class="placeholder" data-placeholder-id="${StringUtils.escapeRegex(
        variableId
      )}".*?<\\/span>`,
      "g"
    );
    const type = variableId.substring(variableId.lastIndexOf("|") + 1);
    if (type === "number") {
      replaceWith = StringUtils.formatNumber(
        Number(replaceWith),
        undefined,
        undefined,
        0,
        2
      );
    }
    if (type === "currency" && replaceWith) {
      replaceWith = StringUtils.formatCurrency(Number(replaceWith));
    }
    if (type === "date" && replaceWith) {
      replaceWith = StringUtils.formatDate(replaceWith);
    }

    return text.replaceAll(regex, replaceWith);
  }
  getPlaceholderFromHtml(text: string) {
    const regex = new RegExp(
      `<span class="placeholder" data-placeholder-id="(.*?)".*?<\\/span>`,
      "g"
    );

    const matches = text.match(regex);
    if (!matches) {
      return [];
    }

    return _.uniq(
      matches.map((match) => {
        const variableId = match.match(/data-placeholder-id="(.*?)"/)?.[1];
        return variableId || null;
      })
    );
  }

  replaceVariableInHtml(
    text: string,
    variableId: string,
    replaceWith: any,
    options?: { keepSpanClasses?: boolean; currentFieldEditing?: string }
  ) {
    const { keepSpanClasses, currentFieldEditing } = options || {};

    const type = variableId.substring(variableId.lastIndexOf("|") + 1);
    let value = replaceWith;
    if (type === "number") {
      value = StringUtils.formatNumber(replaceWith, undefined, undefined, 0, 2);
    }
    if (type === "currency") {
      value = StringUtils.formatCurrency(replaceWith);
    }
    if (type === "date") {
      value = StringUtils.formatDate(replaceWith);
    }

    // Case 1: Default – neither keepSpanClasses nor currentFieldEditing provided.
    if (!keepSpanClasses && !currentFieldEditing) {
      const regex = new RegExp(
        `<span\\s+class="mention-variable"\\s+data-variable-id="${variableId}"[^>]*>.*?<\\/span>`,
        "g"
      );
      return text.replaceAll(regex, value);
    }

    // Case 2: keepSpanClasses is true – keep all spans, add "keep" class to all,
    // and if currentFieldEditing matches, add "mention-variable-current-edit".
    if (keepSpanClasses) {
      const regex = new RegExp(
        `(<span\\s+[^>]*data-variable-id="${variableId}"[^>]*>)(.*?)(<\\/span>)`,
        "g"
      );
      return text.replaceAll(
        regex,
        (match, openingTag, innerContent, closingTag) => {
          // Update the class attribute to always include "keep"
          let newOpeningTag = openingTag.replace(
            /class="([^"]*)"/,
            (m, classNames) => {
              let newClasses = classNames;
              if (!newClasses.includes("keep")) {
                newClasses += " keep";
              }
              if (
                currentFieldEditing === variableId &&
                !newClasses.includes("mention-variable-current-edit")
              ) {
                newClasses += " mention-variable-current-edit";
              }
              return `class="${newClasses.trim()}"`;
            }
          );
          return `${newOpeningTag}${value}${closingTag}`;
        }
      );
    }

    // Case 3: Only currentFieldEditing is provided (and keepSpanClasses is false):
    // For the matching variable, keep the span (removing the default "mention-variable"
    // class) and add "mention-variable-current-edit". For others, remove the span.
    if (currentFieldEditing) {
      if (variableId === currentFieldEditing) {
        const regex = new RegExp(
          `(<span\\s+[^>]*data-variable-id="${variableId}"[^>]*>)(.*?)(<\\/span>)`,
          "g"
        );
        return text.replaceAll(
          regex,
          (match, openingTag, innerContent, closingTag) => {
            let newOpeningTag = openingTag.replace(
              /class="([^"]*)"/,
              (m, classNames) => {
                let newClasses = classNames
                  .split(" ")
                  .filter((cls) => cls !== "mention-variable")
                  .join(" ");
                newClasses += " mention-variable-current-edit";
                return `class="${newClasses.trim()}"`;
              }
            );
            return `${newOpeningTag}${value}${closingTag}`;
          }
        );
      } else {
        const regex = new RegExp(
          `<span\\s+class="mention-variable"\\s+data-variable-id="${variableId}"[^>]*>.*?<\\/span>`,
          "g"
        );
        return text.replaceAll(regex, value);
      }
    }

    return text;
  }

  replaceVariables(
    text: string,
    variables: { [key: string]: unknown },
    options?: {
      keepSpanClasses?: boolean;
      currentFieldEditing?: string;
      formatFunctions?: { [key: string]: (value: any) => string };
      conditions?: { [key: string]: boolean };
    }
  ) {
    let output = text;
    for (const [key, value] of Object.entries(variables)) {
      let formattedValue = value as string;
      if (formattedValue) {
        if (options?.formatFunctions && options.formatFunctions[key]) {
          formattedValue = options.formatFunctions[key](value) as string;
        }
        output = this.replaceVariableInHtml(
          output,
          key,
          formattedValue,
          options
        );
      }
    }
    if (options?.conditions) {
      output = this.replaceConditionsInHtml(output, options.conditions);
    }
    return output;
  }

  // New function to process condition variables.
  // For each span with a data-condition-id that matches a key in conditions:
  // - If the value is true, remove only the span (keep inner content).
  // - If the value is false, remove the span and its content.
  replaceConditionsInHtml(
    text: string,
    conditions: { [key: string]: boolean }
  ): string {
    const regex =
      /<span\s+[^>]*data-condition-id="([^"]+)"[^>]*>([\s\S]*?)<\/span>/g;
    return text.replace(regex, (match, conditionId, innerContent) => {
      if (conditions.hasOwnProperty(conditionId)) {
        return conditions[conditionId] ? innerContent : "";
      }
      // If the condition key is not present, leave the match unchanged.
      return match;
    });
  }

  replacePlaceholders(text: string, placeholders: { [key: string]: string }) {
    let output = text;
    for (const [key, value] of Object.entries(placeholders)) {
      output = this.replacePlaceholdersInHtml(output, key, value);
    }

    return output;
  }
}

const TextEditorUtils = new TextEditorUtilsClass();
export default TextEditorUtils;
(window as any).TextEditorUtils = TextEditorUtils; // for debugging purposes
